import { Autocomplete, TextField } from '@mui/material'
import { useDebounce } from '@uidotdev/usehooks'
import React, { useEffect, useState } from 'react'
import CustomDateTimePicker from '../../../components/custom/CustomDateTimePicker'
import CustomFormInput from '../../../components/custom/CustomInput/custom-form-input'
import CustomFormSelect from '../../../components/custom/CustomSelect/custom-form-select'
import { RInputDeclare } from '../../../components/custom/RInputDeclare'
import menuService from '../../../services/menuService'
import retailService from '../../../services/retailService'
import { couponsModalText } from './CouponModal'

const CouponsForm = ({
	state,
	setState,
	handleChangeType,
	isRetail,
	handleChangeStart,
	handleChangeEnd,
	isVenueTypeOnlineShop,
	handleChangeSelectedProduct,
	handleChangeSelectedCustomer
}) => {

	const [productSearchTerm, setProductSearchTerm] = useState('')
	const [productOptions, setProductOptions] = useState([])
	const debouncedProductSearchTerm = useDebounce(productSearchTerm, 1000);
	const [customerSearchTerm, setCustomerSearchTerm] = useState('')
	const [customerOptions, setCustomerOptions] = useState([])

	const debouncedCoustmerSearchTerm = useDebounce(customerSearchTerm, 1000);

	// Create product value object only if there's a selected product with an id
	const productDefaultValue = state?.selected_product && state.selected_product.id !== undefined && state.selected_product.id !== null
		? { value: state.selected_product.id, label: state.selected_product.title || '' }
		: null;

	// Create customer value object only if there's a selected customer with an id
	const customerDefaultValue = state?.selected_customer && state.selected_customer.id !== undefined && state.selected_customer.id !== null
		? { value: state.selected_customer.id, label: state.selected_customer.name || '' }
		: null;

	const typeOptions = [
		{
			label: 'Percentage cart discount',
			value: 'percentage_cart_discount',
		},
		{
			label: 'Fixed cart discount',
			value: 'fixed_cart_discount',
		},
	]

	const getProducts = () => {
		const query = `search=${productSearchTerm}`
		menuService.getProducts(query)
			.then((res) => {
				setProductOptions(res?.products?.map((item) => {
					return {
						label: item?.title,
						value: item?.id
					}
				}))
			})
			.catch((err) => {
				// Handle error
				setProductOptions([])
			})
	}

	useEffect(() => {
		if (productSearchTerm) {
			getProducts()
		} else {
			setProductOptions([])
		}
	}, [debouncedProductSearchTerm])

	const getCustomers = () => {
		const query = `search=${customerSearchTerm}`
		retailService.GetCustomers(query, {})
			.then((res) => {
				setCustomerOptions(res?.customers?.map((item) => {
					return {
						label: item?.name,
						value: item?.user?.id
					}
				}))
			})
			.catch((err) => {
				// Handle error
				setCustomerOptions([])
			})
	}

	useEffect(() => {
		if (customerSearchTerm) {
			getCustomers()
		} else {
			setCustomerOptions([])
		}
	}, [debouncedCoustmerSearchTerm])

	// Safe handler for product selection changes
	const safeHandleChangeSelectedProduct = (newValue) => {
		// Create a safe object regardless of whether newValue is null or not
		const safeValue = newValue
			? { value: newValue.value, label: newValue.label || '' }
			: { value: null, label: '' };

		handleChangeSelectedProduct(safeValue);
	};

	// Safe handler for customer selection changes
	const safeHandleChangeSelectedCustomer = (newValue) => {
		// Create a safe object regardless of whether newValue is null or not
		const safeValue = newValue
			? { value: newValue.value, label: newValue.label || '' }
			: { value: null, label: '' };

		handleChangeSelectedCustomer(safeValue);
	};

	return (
		<div className="flex flex-col gap-2 overflow-auto max-h-[60vh]">
			<CustomFormInput
				value={state?.code}
				onChange={(e) => setState({ ...state, code: e.target.value })}
				title={couponsModalText('code_optional')}
				placeholder={couponsModalText('enter_code')}
			/>

			<CustomFormInput
				value={state?.description}
				onChange={(e) => setState({ ...state, description: e.target.value })}
				placeholder={couponsModalText('description')}
			/>

			<CustomFormSelect
				onChange={handleChangeType}
				value={state?.discount_type}
				placeholder={couponsModalText('choose_type')}
				options={typeOptions}
				title={couponsModalText('discount_type')}
			/>

			<RInputDeclare
				value={couponsModalText('value')}
				className="!text-[16px] !mb-0 !mt-0 !text-black1 font-medium"
			/>
			<div className="flex flex-row items-center border rounded-md h-10">
				<span className="flex items-center border-r border-0 px-4 h-10">
					{state?.discount_type
						? state?.discount_type === 'fixed_cart_discount'
							? ''
							: '%'
						: ''}
				</span>
				<input
					type="number"
					className="border-0 outline-0 flex-1 px-4"
					value={state?.discount_amount}
					onChange={(e) =>
						setState((v) => ({
							...v,
							discount_amount: parseInt(e.target.value),
						}))
					}
				/>
			</div>

			{isRetail() && (
				<>
					<RInputDeclare
						value={couponsModalText('min_spent')}
						className="!text-[16px] !mb-0 !mt-0 !text-black1 font-medium"
					/>
					<TextField
						required
						type="number"
						size="small"
						value={state?.minimum_spent}
						placeholder={couponsModalText('enter_min_spent')}
						onChange={(e) =>
							setState({ ...state, minimum_spent: e.target.value })
						}
					/>

					<RInputDeclare
						value={couponsModalText('max_spent')}
						className="!mt-6 !mb-2 !text-black1 font-medium"
					/>
					<TextField
						required
						type="number"
						size="small"
						value={state?.maximum_spent}
						placeholder={couponsModalText('enter_max_spent')}
						onChange={(e) =>
							setState({ ...state, maximum_spent: e.target.value })
						}
					/>

					<RInputDeclare
						value={couponsModalText('usage_limit_per_customer')}
						className="!mt-6 !mb-2 !text-black1 font-medium"
					/>
					<TextField
						required
						type="number"
						size="small"
						value={state?.usage_limit_per_customer}
						placeholder={couponsModalText(
							'usage_limit_per_customer_placeholder'
						)}
						onChange={(e) =>
							setState({
								...state,
								usage_limit_per_customer: e.target.value,
							})
						}
					/>

					<RInputDeclare
						value={couponsModalText('usage_limit_per_coupon')}
						className="!mt-6 !mb-2 !text-black1 font-medium"
					/>
					<TextField
						required
						type="number"
						size="small"
						value={state?.usage_limit_per_coupon}
						placeholder={couponsModalText('usage_limit_per_coupon_placeholder')}
						onChange={(e) =>
							setState({
								...state,
								usage_limit_per_coupon: e.target.value,
							})
						}
					/>
				</>
			)}

			{
				isVenueTypeOnlineShop() && (
					<>
						<RInputDeclare
							value={couponsModalText('specific_product')}
							className="!text-[16px] !mb-0 !mt-2 !text-black1 font-medium"
						/>
						<Autocomplete
							disablePortal
							options={productOptions}
							value={productDefaultValue}
							onInputChange={(e, v) => {
								if (e?.target) {
									setProductSearchTerm(e.target.value || '');
								}
							}}
							onChange={(event, newValue) => safeHandleChangeSelectedProduct(newValue)}
							renderInput={(params) => <TextField {...params} size='small' placeholder='Search for a product' />}
							noOptionsText="No products found"
						/>

						<RInputDeclare
							value={couponsModalText('usage_limit_per_customer')}
							className="!text-[16px] !mb-0 !mt-2 !text-black1 font-medium"
						/>
						<TextField
							required
							type="number"
							size="small"
							value={state?.usage_limit_per_coupon}
							placeholder={couponsModalText('usage_limit_per_customer_placeholder')}
							onChange={(e) =>
								setState({
									...state,
									usage_limit_per_coupon: e.target.value,
								})
							}
						/>

						<RInputDeclare
							value={couponsModalText('specific_customer')}
							className="!text-[16px] !mb-0 !mt-2 !text-black1 font-medium"
						/>
						<Autocomplete
							disablePortal
							options={customerOptions}
							value={customerDefaultValue}
							onInputChange={(e, v) => {
								if (e?.target) {
									setCustomerSearchTerm(e.target.value || '');
								}
							}}
							onChange={(event, newValue) => safeHandleChangeSelectedCustomer(newValue)}
							renderInput={(params) => <TextField {...params} size='small' placeholder='Search for a customer' />}
							noOptionsText="No customers found"
						/>

						<RInputDeclare
							value={couponsModalText('min_spent')}
							className="!text-[16px] !mb-0 !mt-2 !text-black1 font-medium"
						/>
						<TextField
							required
							type="number"
							size="small"
							value={state?.minimum_spent}
							placeholder={couponsModalText('enter_min_spent')}
							onChange={(e) =>
								setState({ ...state, minimum_spent: e.target.value })
							}
						/>
					</>
				)
			}

			<RInputDeclare
				value={couponsModalText('start_time')}
				className="!text-[16px] !mb-0 !mt-2 !text-black1 font-medium"
			/>
			<CustomDateTimePicker
				placeholder={couponsModalText('start_time')}
				value={state?.start_time}
				onChange={handleChangeStart}
			/>

			<RInputDeclare
				value={couponsModalText('end_time')}
				className="!text-[16px] !mb-0 !mt-2 !text-black1 font-medium"
			/>
			<CustomDateTimePicker
				placeholder={couponsModalText('end_time')}
				value={state?.expiry_time}
				onChange={handleChangeEnd}
			/>
		</div>
	)
}

export default CouponsForm